<script lang="ts">
    import {exists, ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {onDestroy, onMount} from 'svelte';
    import {getMainMenuContext} from 'shared/components/kp-main-menu/main-menu-context';
    import {fly} from 'svelte/transition';
    import {get} from 'svelte/store';
    import KpMainMenuNavItem from 'shared/components/kp-main-menu/menu-nav/KpMainMenuNavItem.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {RECALCULATE_NAV_OVERFLOW_EVENT} from 'shared/components/kp-main-menu/types';

    const context = getMainMenuContext();
    let mainMenuNavElement: HTMLElement;
    let menuItems = get(context.items);
    let navScrollLeft = 0;
    let navScrollWidth = 0;
    let navWidth = 0;
    let innerWidth = 0;
    let horizontalOverflowing = false;
    let overflowLeft = false;
    let overflowRight = false;
    const menuItemsUnsubscribe = context.items.subscribe((currentItems) => menuItems = currentItems);
    const searchOpenUnsubscribe = context.isNavSearchOpen.subscribe(() => calculateOverflowValues());
    const stuckToTopUnsubscribe = context.isStuckToTop.subscribe(() => setTimeout(calculateOverflowValues, context.searchFadeAnimDuration + 10));
    $: calculateOverflowValues(innerWidth);

    onMount(() => {
        context.eventBus.addEventListener(RECALCULATE_NAV_OVERFLOW_EVENT, handleRecalculateNavOverflow);
        calculateOverflowValues();
    });

    onDestroy(() => {
        searchOpenUnsubscribe();
        stuckToTopUnsubscribe();
        menuItemsUnsubscribe();
        context.eventBus.removeEventListener(RECALCULATE_NAV_OVERFLOW_EVENT, handleRecalculateNavOverflow);
    });

    const handleRecalculateNavOverflow = () => {
        calculateOverflowValues();
    }

    function calculateOverflowValues(pageWidth?: number) {
        // pageWidth parameter is there just to trigger reactivity
        // for recalculating overflow values on window resize,
        // so we can just ignore it
        ignoreUnusedProperties(pageWidth);

        if (!mainMenuNavElement) {
            return;
        }

        navScrollLeft = mainMenuNavElement.scrollLeft;
        navWidth = mainMenuNavElement.clientWidth;
        navScrollWidth = mainMenuNavElement.scrollWidth;

        if (navScrollWidth <= navWidth) {
            navScrollLeft = 0;
        }

        horizontalOverflowing = navScrollWidth > navWidth;
        overflowLeft = navScrollLeft > 0;
        overflowRight = navScrollLeft + navWidth < navScrollWidth - 1;
    }

    const handleScroll = () => {
        calculateOverflowValues();
    }

    const handleScrollLeftClick = () => {
        mainMenuNavElement.scrollTo({
            left: Math.max(0, navScrollLeft - navWidth),
            behavior: 'smooth'
        });
    }

    const handleScrollRightClick = () => {
        mainMenuNavElement.scrollTo({
            left: Math.min(Math.max(0, navScrollLeft + navWidth - 150), navScrollWidth - navWidth),
            behavior: 'smooth'
        });
    }
</script>

<svelte:window bind:innerWidth/>

<div class="kp-main-menu-nav-wrapper">
    <ul class="kp-main-menu-nav"
        bind:this={mainMenuNavElement}
        on:scroll={handleScroll}>

        {#each menuItems as menuItem}
            {#if exists(menuItem.id)}
                <KpMainMenuNavItem {menuItem}/>
            {/if}
        {/each}
    </ul>

    {#if horizontalOverflowing}
        {#if overflowLeft}
            <button type="button"
                    tabindex="-1"
                    aria-hidden="true"
                    class="overflow-btn scroll-left"
                    transition:fly={{x: -15}}
                    on:click={handleScrollLeftClick}>

                <span class="icon-container">
                    <UIcon icon="arrow-left"/>
                </span>
            </button>
        {/if}

        {#if overflowRight}
            <button type="button"
                    tabindex="-1"
                    aria-hidden="true"
                    class="overflow-btn scroll-right"
                    transition:fly={{x: 15}}
                    on:click={handleScrollRightClick}>

                <span class="icon-container">
                    <UIcon icon="arrow-right"/>
                </span>
            </button>
        {/if}
    {/if}
</div>

<style lang="less">
    .kp-main-menu-nav-wrapper,
    .kp-main-menu-nav {
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .kp-main-menu-nav-wrapper {
        position: relative;
        flex: 1;
        height: 100%;
        display: flex;
        overflow-x: hidden;

        .kp-main-menu-nav {
            width: 100%;
            height: 100%;
            display: flex;
            overflow-x: auto;
        }

        .overflow-btn {
            top: 0;
            height: 100%;
            width: 48px;
            position: absolute;
            outline: none;
            background: none;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;

            .icon-container {
                font-size: 16px;
                transition: transform 0.3s ease-in-out;
            }

            &.scroll-left {
                left: 0;
                justify-content: start;
                background: linear-gradient(to right, var(--main-menu-background-color) 50%, rgba(255, 255, 255, 0) 100%);

                &:hover .icon-container {
                    transform: translateX(-5px);
                }
            }

            &.scroll-right {
                right: 0;
                justify-content: end;
                background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, var(--main-menu-background-color) 50%);

                &:hover .icon-container {
                    transform: translateX(5px);
                }
            }
        }
    }

    @media screen and (max-width: 720px) {
        .kp-main-menu-nav-wrapper {
            display: none;
        }
    }
</style>